
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import secureLocalStorage from "./storage";
import { toast } from "react-toastify";
import Footer from "./Footer";
import "react-toastify/dist/ReactToastify.css";
import "./DealDetail.css";
import DealImage, { uploadUrl } from "./DealImage";
const DealDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [clockOffset, setClockOffset] = useState(0); // server time minus browser time

  useEffect(() => {
    const fetchDeal = async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API}website/deal/${slug}`
        );
        if (data?.success) {
          setDeal(data.deal);
          setIsExpired(!!data.is_expired);
          if (data.server_time) {
            setClockOffset(new Date(data.server_time).getTime() - Date.now());
          }
        } else {
          setFetchError(true);
        }
      } catch (e) {
        console.error(e);
        toast.error("Can not load deal, please try again");
        setFetchError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchDeal();
  }, [slug]);

  useEffect(() => {
    // Only flash deals have a countdown
    if (!deal?.is_flash_deal || !deal?.expiry_time) return;

    const tick = () => {
      const diff = new Date(deal.expiry_time).getTime() - (Date.now() + clockOffset);
      if (diff <= 0) {
        setTimeLeft("EXPIRED");
        setIsExpired(true);
        return;
      }
      const h = String(Math.floor(diff / 3600000)).padStart(2, "0");
      const m = String(Math.floor((diff % 3600000) / 60000)).padStart(2, "0");
      const s = String(Math.floor((diff % 60000) / 1000)).padStart(2, "0");
      setTimeLeft(`${h}:${m}:${s}`);
    };

    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [deal, clockOffset]);

  const handleGrabDeal = () => {
    if (isExpired) {
      toast.error("This deal has expired");
      return;
    }
    const userId = secureLocalStorage.getItem("n_id");
    const token = secureLocalStorage.getItem("n_token");
    if (!userId || !token) {
      toast.info("Please log in first to grab this deal.");
      navigate("/login");
      return;
    }
    window.open(`/visit/deal/${deal._id}`, "_blank");
  };

  if (loading) return <p className="text-center py-5">Loading...</p>;
  if (fetchError || !deal)
    return <p className="text-center py-5">Deal not found</p>;

  const isFlash = deal.is_flash_deal;
  const savings =
    Number(deal.mrp) > Number(deal.price)
      ? Number(deal.mrp) - Number(deal.price)
      : 0;

  return (
    <div className="deal-page">
      <div className="container py-4">
        {/* FLASH DEAL header: sirf flash deals ke liye */}
        {isFlash && (
          <div className="deal-flash-wrap">
            <h2 className="deal-flash-label">FLASH DEAL</h2>
            {timeLeft && (
              <div className="deal-timer">
                <i className="las la-clock deal-timer-icon"></i>
                {timeLeft === "EXPIRED" ? "Deal Expired" : `Ends in ${timeLeft}`}
              </div>
            )}
          </div>
        )}
        {isExpired && (
          <div className="deal-expired-banner" role="alert">
            This deal has expired. Check out our other live deals!
            <button type="button" onClick={() => navigate("/flash-deals")}>
              View live deals
            </button>
          </div>
        )}
        <div className="deal-row">
          {/* LEFT COLUMN */}
          <div className="deal-left">
            <div className="deal-image-wrap">
              {deal.store_id?.logo && (
                <div className="deal-logo-overlay">
                  <img
                    className="deal-logo-img"
                    src={uploadUrl(deal.store_id.logo)}
                    alt={deal.store_id.name}
                    onError={(e) => {
                      // hide the whole white badge if the logo is missing
                      e.currentTarget.parentElement.style.display = "none";
                    }}
                  />
                </div>
              )}

              {deal.offer_percent > 0 && (
                <div className="deal-percent-overlay">-{deal.offer_percent}% OFF</div>
              )}

              <DealImage file={deal.image} alt={deal.title} className="deal-image" />
            </div>

            {/* Coupon box: sirf flash deals ke liye */}
            {isFlash && deal.coupon_code && !isExpired && (
              <div className="deal-coupon">
                <div>
                  <small className="deal-coupon-label">Use Code</small>
                  <strong className="deal-coupon-code">{deal.coupon_code}</strong>
                </div>
                <button
                  className="deal-copy-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(deal.coupon_code);
                    toast.success("Code copied!");
                  }}
                >
                  <i className="las la-copy deal-copy-icon"></i>
                  Copy Code
                </button>
              </div>
            )}

            {deal.store_id && (
              <div className="deal-store-row">
                <span className="deal-store-name">{deal.store_id.name}</span>
              </div>
            )}

            <button
              className="deal-grab-btn"
              onClick={handleGrabDeal}
              disabled={isExpired}
            >
              {isExpired ? "Deal Expired" : "Grab Deal →"}
            </button>
          </div>

          {/* RIGHT COLUMN */}
          <div className="deal-right">
            <h1 className="deal-title">{deal.title}</h1>

            <div className="deal-price-row">
              {deal.price && <span className="deal-price">₹{deal.price}</span>}
              {deal.mrp && <span className="deal-mrp">₹{deal.mrp}</span>}
              {savings > 0 && (
                <span className="deal-save">You save ₹{savings}</span>
              )}
            </div>

            {isFlash ? (
              <>
                <div className="deal-block">
                  <h5 className="deal-heading">{deal.discount_text}</h5>
                  <ul className="deal-list">
                    {deal.highlight_bullets?.map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>
                </div>

                <div className="deal-block">
                  <h5 className="deal-heading">Why you'll like it</h5>
                  <ul className="deal-list">
                    {deal.why_bullets?.map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>
                </div>

                <div className="deal-cashback-box">
                  <h5 className="deal-heading">
                    {deal.cashback_text}, over and above the deal
                  </h5>
                  <div className="deal-chips">
                    <span className="deal-chip">
                      Tracking Time: {deal.tracking_time} {deal.tracking_unit}
                    </span>
                    <span className="deal-chip">
                      Confirmation Timeline: {deal.confirm_time} {deal.confirm_unit}
                    </span>
                  </div>
                </div>

                <div className="deal-block">
                  <h5 className="deal-heading">Grab it in 3 steps</h5>
                  <ol className="deal-steps">
                    {deal.grab_steps?.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ol>
                </div>

                {deal.good_to_know?.length > 0 && (
                  <div className="deal-block deal-note">
                    <h5 className="deal-heading">Good to know before you buy</h5>
                    <ul className="deal-list">
                      {deal.good_to_know.map((g, i) => (
                        <li key={i}>{g}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              // ================= NORMAL DEAL CONTENT =================
              <>
                {deal.description && (
                  <div className="deal-block">
                    <h5 className="deal-heading">About the Product</h5>
                    <p className="deal-text">{deal.description}</p>
                  </div>
                )}

                {deal.highlight_bullets?.length > 0 && (
                  <div className="deal-block">
                    <h5 className="deal-heading">About the Offer</h5>
                    <ul className="deal-list">
                      {deal.highlight_bullets.map((b, i) => (
                        <li key={i}>{b}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {deal.store_id?.cashback_categories?.length > 0 && (
                  <div className="deal-block">
                    <h5 className="deal-heading">Cashback Related</h5>
                    <ul className="deal-list">
                      {deal.store_id.cashback_categories.map((c, i) => (
                        <li key={i}>
                          {c.category}: {c.percent}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="deal-cashback-box">
                  <h5 className="deal-heading">Tracking Information</h5>
                  <div className="deal-chips">
                    <span className="deal-chip">
                      Tracking Time: {deal.tracking_time} {deal.tracking_unit}
                    </span>
                    <span className="deal-chip">
                      Confirmation Timeline: {deal.confirm_time} {deal.confirm_unit}
                    </span>
                  </div>
                </div>

                {deal.good_to_know?.length > 0 && (
                  <div className="deal-block deal-note">
                    <h5 className="deal-heading">Important Information</h5>
                    <ul className="deal-list">
                      {deal.good_to_know.map((g, i) => (
                        <li key={i}>{g}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {deal.grab_steps?.length > 0 && (
                  <div className="deal-block">
                    <h5 className="deal-heading">How to get this offer</h5>
                    <ol className="deal-steps">
                      {deal.grab_steps.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ol>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default DealDetail;